import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Combination, Term } from '../../Data/types';
import { ParticleCanvas } from '../ParticleCanvas/ParticleCanvas';

export interface NewCombinationPopupState {
  id: number;
  term1: Term;
  term2: Term;
  combination: Combination;
  product: Term;
}

interface NewCombinationPopupProps {
  state: NewCombinationPopupState | null;
  onNext: () => void;
}

export const NewCombinationPopup: React.FC<NewCombinationPopupProps> = ({ state, onNext }) => {
  return (
    <AnimatePresence>
      {state && (
        <motion.div
          key={state.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.92)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.05 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 28,
              maxWidth: 340,
              width: '100%',
              padding: '0 24px',
            }}
          >
            {/* Term pair with glow and particles */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 280, damping: 20, delay: 0.1 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <TermCard term={state.term1} />
              <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.35)', fontWeight: 300 }}>+</div>
              <TermCard term={state.term2} />
            </motion.div>

            {/* Justification clue */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.28 }}
              style={{
                fontSize: 16,
                color: '#a5b4fc',
                fontStyle: 'italic',
                textAlign: 'center',
                lineHeight: 1.55,
                textShadow: '0 1px 8px rgba(0,0,0,0.5)',
              }}
            >
              {state.combination.combinationJustification}
            </motion.div>

            {/* Next button */}
            <motion.button
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.42 }}
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              whileTap={{ scale: 0.95 }}
              style={{
                marginTop: 4,
                padding: '12px 36px',
                borderRadius: 14,
                border: '2px solid rgba(99,102,241,0.7)',
                background: 'rgba(99,102,241,0.18)',
                color: '#a5b4fc',
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: '0.08em',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              Unlock "{state.product.displayName}"
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const TermCard: React.FC<{ term: Term }> = ({ term }) => (
  <div style={{ position: 'relative' }}>
    {/* Particle halo — forever ambient anticipation */}
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(99,102,241,0.12)',
        border: '2px solid rgba(99,102,241,0.55)',
        borderRadius: 16,
        padding: '10px 14px',
        minWidth: 80,
        boxShadow: '0 0 20px 6px rgba(99,102,241,0.3)',
      }}
    >
      <span style={{ fontSize: 36, lineHeight: 1 }}>{term.emoji}</span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'rgba(255,255,255,0.8)',
          textAlign: 'center',
          letterSpacing: '0.03em',
        }}
      >
        {term.displayName}
      </span>
    </div>
  </div>
);
